import type { CSSProperties } from 'react';

import { resolveVariant } from '@/features/storefront/layouts/families';
import type { FocalPoint } from '@/features/storefront/sections/schema';
import { effectiveTokens } from '@/features/storefront/tokens';
import type { StorefrontTheme } from '@/features/storefront/themeSchema';
import { contrastRatio, isHexColor, READABLE_LIGHT, readableTextOn } from '@/lib/color';

/**
 * A theme document turned into CSS custom properties.
 *
 * Section components read `var(--storefront-…)` and never take a theme prop.
 * That is what lets one set of components render both the live storefront and
 * the dashboard's preview: the preview sets these variables on a wrapper, the
 * storefront sets them on its shell, and nothing downstream knows which it is
 * inside. A preview built from its own approximation of the theme is a preview
 * that drifts, and merchants find the drift after they publish.
 */
/** White under this much contrast on the accent gives way to the readable ink. */
const ON_ACCENT_MIN_CONTRAST = 3;

function onAccent(accent: string): string {
  if (!isHexColor(accent) || contrastRatio(READABLE_LIGHT, accent) >= ON_ACCENT_MIN_CONTRAST) return READABLE_LIGHT;
  return readableTextOn(accent);
}

/** The share of a wide screen the page column may take, and its ceiling. */
const FLUID_PAGE_SHARE = '92vw';
const FLUID_PAGE_CEILING = '120rem';

/**
 * A family's page column, allowed to grow with a wide screen.
 *
 * A family's `containerMax` was drawn for a laptop, and there it still holds:
 * below it the column is simply the screen less its gutter. On a 1920px or
 * wider monitor, though, a fixed 82rem left a third of the screen as empty
 * margin. So the column is the larger of the family's width and most of the
 * screen, stopping at a ceiling where lines and grids would sprawl. Phones and
 * laptops never reach either arm, so they render exactly as before.
 */
export function fluidContainerMax(containerMax: string): string {
  return `max(${containerMax}, min(${FLUID_PAGE_SHARE}, ${FLUID_PAGE_CEILING}))`;
}

export function themeVariables(theme: StorefrontTheme): CSSProperties {
  const solid = theme.buttons.style === 'solid';
  // The family's tokens, under a built theme's own overrides where it has any.
  const family = effectiveTokens(theme);
  // The header can be mixed in from another family, and it is the header's own
  // height a full-screen banner has to subtract - not the page family's. A
  // built theme's own header height still wins: it was measured off the
  // design the theme was drawn from.
  const header = effectiveTokens(theme, resolveVariant(theme.layout, 'header'));
  // The merchant's explicit heading face wins; then the family's; then the
  // body face - which is what every theme rendered before heading fonts.
  const headingFont = theme.typography.heading_font ?? family.headingFont ?? theme.typography.font_family;

  return {
    backgroundColor: theme.colors.background,
    color: theme.colors.text,
    fontFamily: theme.typography.font_family,
    fontSize: `${theme.typography.base_size_px}px`,
    '--storefront-primary': theme.colors.primary,
    // The family's own warm note, or the theme's primary where the family has
    // none - so a component can reach for the accent without first asking
    // which family it is rendering inside.
    '--storefront-accent': family.accent ?? theme.colors.primary,
    '--storefront-background': theme.colors.background,
    '--storefront-surface': theme.colors.surface,
    '--storefront-text': theme.colors.text,
    '--storefront-muted': theme.colors.muted,
    // The palette again, under names no area redeclares: a header drawn in its
    // own colours still means the theme's primary when it says "primary".
    '--storefront-palette-primary': theme.colors.primary,
    '--storefront-palette-background': theme.colors.background,
    '--storefront-palette-surface': theme.colors.surface,
    '--storefront-palette-text': theme.colors.text,
    '--storefront-palette-muted': theme.colors.muted,
    // The accent as an area colour choice ("accent"), under the same rule.
    '--storefront-palette-accent': family.accent ?? theme.colors.primary,
    // What is written on the accent - the sale badge. White, as every theme
    // was drawn, unless the accent is too light to carry it (a lime, a sunny
    // yellow), where white text simply disappears.
    '--storefront-palette-on-accent': onAccent(family.accent ?? theme.colors.primary),
    '--storefront-radius': `${theme.buttons.radius_px}px`,
    '--storefront-heading-font': headingFont,
    // The family's machine face. `inherit` where the family has none, so a
    // shared component can set a price in mono without first asking which
    // family it is rendering inside - the families that had no such face keep
    // rendering in the body face, exactly as they did before this existed.
    '--storefront-mono-font': family.monoFont ?? 'inherit',
    '--storefront-heading-weight': theme.typography.heading_weight,
    '--storefront-heading-tracking': family.headingTracking,
    '--storefront-heading-transform': family.headingTransform,
    // The family's second heading face, for the word a heading leans on. Like
    // the mono face, `inherit` where the family has none - so the renderer can
    // wrap an emphasised word unconditionally and the families that never
    // asked for a display face render it in the heading face, unchanged.
    '--storefront-display-font': family.displayFont ?? 'inherit',
    // Where a section heading stands, as the two properties that decide it:
    // the text's own alignment, and whether its column is centred in the page.
    '--storefront-heading-align': family.headingAlign ?? 'left',
    '--storefront-heading-inset': family.headingAlign === 'center' ? 'auto' : '0',
    '--storefront-container-max': fluidContainerMax(family.containerMax),
    '--storefront-header-height': header.headerHeight,
    '--storefront-space-scale': family.spaceScale,
    // A coloured section and the footer as cards in the page column, when the
    // theme asks (`bandFrame`): their width, corners and the page colour
    // between them. Unset otherwise, so every band runs edge to edge as before.
    // The widths subtract the column's gutter (`StorefrontContainer`'s px-5,
    // sm:px-8), so a card's edges are the edges every section's content has.
    ...(family.bandFrame === 'card'
      ? {
          '--storefront-band-max': 'min(calc(100% - 2.5rem), calc(var(--storefront-container-max) - 2.5rem))',
          '--storefront-band-max-sm': 'min(calc(100% - 4rem), calc(var(--storefront-container-max) - 4rem))',
          '--storefront-band-radius': 'clamp(1rem, var(--storefront-radius), 2rem)',
          '--storefront-band-gap': '1.25rem',
          '--storefront-band-clip': 'hidden',
        }
      : {}),
    // Stars under a product card, only where the family asks for them.
    '--storefront-card-ratings': family.cardRatings ? 'block' : 'none',
    // The bottom edge of a full-width hero: none, or a diagonal cut that lets
    // the page colour rise into the picture - deepest at the left.
    '--storefront-hero-clip':
      family.heroEdge === 'slant' ? 'polygon(0 0, 100% 0, 100% 94%, 0 80%)' : 'none',
    // The two halves of a button, resolved once here: a solid button's label
    // sits on the primary colour, an outline button's on the page itself.
    '--storefront-button-bg': solid ? theme.colors.primary : 'transparent',
    '--storefront-button-text': solid ? theme.colors.background : theme.colors.primary,
  } as CSSProperties;
}

/** Every section's button, so a theme's shape and colour apply to all of them. */
export const STOREFRONT_BUTTON_STYLE: CSSProperties = {
  backgroundColor: 'var(--storefront-button-bg)',
  color: 'var(--storefront-button-text)',
  border: '1px solid var(--storefront-primary)',
  borderRadius: 'var(--storefront-radius)',
};

/**
 * The machine details - a price, a stock count, a small-caps announcement.
 *
 * Safe to reach for in any shared component: a family with no mono face
 * resolves the variable to `inherit` and renders exactly as it did before, so
 * only the families that asked for the look actually wear it.
 */
export const STOREFRONT_MONO_STYLE: CSSProperties = {
  fontFamily: 'var(--storefront-mono-font)',
};

/** Headings follow the theme's face, weight and the layout family's voice. */
export const STOREFRONT_HEADING_STYLE: CSSProperties = {
  fontFamily: 'var(--storefront-heading-font)',
  fontWeight: 'var(--storefront-heading-weight)' as CSSProperties['fontWeight'],
  letterSpacing: 'var(--storefront-heading-tracking)',
  textTransform: 'var(--storefront-heading-transform)' as CSSProperties['textTransform'],
};

/**
 * The picture on a catalog or content tile.
 *
 * Tiles keep a fixed aspect box so a grid of them lines up, but a merchant's
 * photos arrive in whatever shape their camera or their supplier gave them.
 * Filling that box crops whatever does not fit - the top of a tall photo, the
 * sides of a wide one - and the merchant never chose where the cut lands. So
 * the picture is fitted inside the box instead, with the theme's surface
 * colour showing around it. The grid stays even and nothing is ever cut.
 *
 * Full-bleed art still covers: a banner, a slideshow, an overlay card whose
 * name and price sit on the picture. Those are backgrounds, and a letterboxed
 * background is a broken one.
 */
export const STOREFRONT_TILE_IMAGE = 'object-contain';

/**
 * A content tile's fit once the merchant may have repositioned its picture.
 *
 * A chosen point is the merchant deciding where the cut lands, which is the
 * one thing fitting protects them from not deciding - so with a point the
 * picture fills its box at that point, and without one it fits as above.
 * "Centre" in the reposition dialog saves no point, so it returns to fitting.
 */
export function tileImageFit(focal: FocalPoint | null | undefined): string {
  return focal ? 'object-cover' : STOREFRONT_TILE_IMAGE;
}
